import type { OptimisticLocalStore } from "convex/browser";
import type { FunctionArgs, FunctionReturnType } from "convex/server";
import { api } from "../../convex/_generated/api";
import type { Doc, Id } from "../../convex/_generated/dataModel";

type DashboardSummary = FunctionReturnType<typeof api.dashboard.getByDate>;
type MealMutationArgs = FunctionArgs<typeof api.meals.logMealBatch>;

function addMetric(current: number | null, added: number | undefined): number | null {
  return added === undefined ? current : (current ?? 0) + added;
}

function sumMetric(
  items: MealMutationArgs["items"],
  field: "proteinG" | "carbsG" | "fatG" | "fiberG",
): number | undefined {
  let total: number | undefined;
  for (const item of items) {
    const value = item[field];
    if (value !== undefined) total = (total ?? 0) + value;
  }
  return total;
}

export function applyOptimisticMealBatch(
  localStore: OptimisticLocalStore,
  args: MealMutationArgs,
): void {
  const knownKcal = args.items.reduce(
    (sum, item) => sum + (item.finalKcal ?? item.estimatedKcal ?? 0),
    0,
  );
  const unknownCount = args.items.filter((item) => item.kcalUnknown === true).length;
  const loggedAt = args.clientLoggedAt ?? Date.now();
  const date = args.clientDayStart ?? args.date;
  const tempEntries: Doc<"foodEntries">[] = args.items.map((item, index) => ({
    _id: `temp_${loggedAt}_${index}_${Math.random().toString(36).slice(2)}` as Id<"foodEntries">,
    _creationTime: loggedAt,
    userId: "optimistic" as Id<"users">,
    date,
    name: item.name,
    ...(item.estimatedKcal === undefined ? {} : { estimatedKcal: item.estimatedKcal }),
    ...(item.finalKcal === undefined && item.estimatedKcal === undefined
      ? {}
      : { finalKcal: item.finalKcal ?? item.estimatedKcal }),
    ...(item.proteinG === undefined ? {} : { proteinG: item.proteinG }),
    ...(item.carbsG === undefined ? {} : { carbsG: item.carbsG }),
    ...(item.fatG === undefined ? {} : { fatG: item.fatG }),
    ...(item.fiberG === undefined ? {} : { fiberG: item.fiberG }),
    mealType: args.mealType,
    ...(item.quantity === undefined ? {} : { quantity: item.quantity }),
    ...(item.unit === undefined ? {} : { unit: item.unit }),
    ...(item.confidence === undefined ? {} : { confidence: item.confidence }),
    ...(item.dataSource === undefined ? {} : { dataSource: item.dataSource }),
    ...(item.kcalUnknown === true ? { kcalUnknown: true } : {}),
    source: args.source,
    status: "logged",
    loggedAt,
  }));
  const applyToSummary = (current: DashboardSummary): DashboardSummary => ({
    ...current,
    consumedKcal: current.consumedKcal + knownKcal,
    netKcal: current.netKcal + knownKcal,
    remainingKcal: current.remainingKcal - knownKcal,
    consumedProteinG: addMetric(current.consumedProteinG, sumMetric(args.items, "proteinG")),
    consumedCarbsG: addMetric(current.consumedCarbsG, sumMetric(args.items, "carbsG")),
    consumedFatG: addMetric(current.consumedFatG, sumMetric(args.items, "fatG")),
    consumedFiberG: addMetric(current.consumedFiberG, sumMetric(args.items, "fiberG")),
    unknownKcalCount: current.unknownKcalCount + unknownCount,
    foodEntries: [...current.foodEntries, ...tempEntries],
  });

  for (const query of localStore.getAllQueries(api.dashboard.getByDate)) {
    if (
      query.value !== undefined &&
      (query.args.clientDayStart ?? query.args.date) === date
    ) {
      localStore.setQuery(api.dashboard.getByDate, query.args, applyToSummary(query.value));
    }
  }
  for (const query of localStore.getAllQueries(api.dashboard.getToday)) {
    if (query.value !== undefined && query.args.clientDayStart === date) {
      localStore.setQuery(api.dashboard.getToday, query.args, applyToSummary(query.value));
    }
  }
}
